import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput from "../../../../components/inputs/FractionInput";

// ===================== helpers =====================

const randInt = (min, max) =>
  Math.floor(Math.random() * (max - min + 1)) + min;

function normalize(str) {
  return String(str ?? "")
    .trim()
    .replace(/\u200b/g, "")
    .replace(/\s+/g, "")
    .replace(/,/g, ".")
    .replace(/[−–]/g, "-");
}

// FractionInput potrafi zwracać obiekt (np. { raw, latex, ... })
// - w UI chcemy mieć zawsze string
function toRawAnswer(userAnswer) {
  if (userAnswer == null) return "";
  if (typeof userAnswer === "string" || typeof userAnswer === "number") {
    return String(userAnswer);
  }
  if (typeof userAnswer === "object") {
    // najczęstsze pola spotykane w takich inputach
    return String(
      userAnswer.raw ??
        userAnswer.value ??
        userAnswer.text ??
        userAnswer.input ??
        userAnswer.latex ??
        ""
    );
  }
  return String(userAnswer);
}

// Prosta ewaluacja liczby / ułamka / dziesiętnej
function evalNumber(raw) {
  const s = normalize(raw);
  if (!s) return NaN;

  // ułamek a/b
  const frac = s.match(/^([+-]?\d+)\/([+-]?\d+)$/);
  if (frac) {
    const a = Number(frac[1]);
    const b = Number(frac[2]);
    if (!Number.isFinite(a) || !Number.isFinite(b) || b === 0) return NaN;
    return a / b;
  }

  // liczba całkowita / dziesiętna
  const v = Number(s);
  return Number.isFinite(v) ? v : NaN;
}

// ===================== wizual: odcinek 0 ↔ a =====================
// Theme-aware: oś/napisy = currentColor, ale odcinek zawsze pomarańczowy

function NumberLineDistance({ a }) {
  const width = 620;
  const height = 92;

  const minVal = Math.min(0, a);
  const maxVal = Math.max(0, a);

  // margines w "wartościach", żeby punkty nie były przy krawędzi
  const pad = Math.max(1, Math.ceil((maxVal - minVal) * 0.25));
  const leftVal = minVal - pad;
  const rightVal = maxVal + pad;

  const mapX = (v) => {
    const t = (v - leftVal) / (rightVal - leftVal);
    return 18 + t * (width - 36);
  };

  const x0 = mapX(0);
  const xa = mapX(a);

  const xL = Math.min(x0, xa);
  const xR = Math.max(x0, xa);

  const yAxis = 40;
  const yTickTop = 32;
  const yTickBot = 48;

  return (
    <svg
      width="100%"
      viewBox={`0 0 ${width} ${height}`}
      style={{ maxWidth: width }}
    >
      {/* oś */}
      <line
        x1="18"
        y1={yAxis}
        x2={width - 18}
        y2={yAxis}
        stroke="currentColor"
        strokeWidth="2"
      />
      {/* strzałka */}
      <polygon
        points={`${width - 18},${yAxis} ${width - 28},${yAxis - 6} ${
          width - 28
        },${yAxis + 6}`}
        fill="currentColor"
        opacity="0.9"
      />

      {/* odcinek 0↔a (STAŁY POMARAŃCZ) */}
      <line
        x1={xL}
        y1={yAxis}
        x2={xR}
        y2={yAxis}
        stroke="#d7a74a"
        strokeWidth="10"
        strokeLinecap="round"
        opacity="0.95"
      />

      {/* punkty */}
      <circle cx={x0} cy={yAxis} r="7" fill="currentColor" />
      <circle cx={xa} cy={yAxis} r="7" fill="currentColor" />

      {/* kreski */}
      <line
        x1={x0}
        y1={yTickTop}
        x2={x0}
        y2={yTickBot}
        stroke="currentColor"
        strokeWidth="2"
      />
      <line
        x1={xa}
        y1={yTickTop}
        x2={xa}
        y2={yTickBot}
        stroke="currentColor"
        strokeWidth="2"
      />

      {/* opisy */}
      <text
        x={x0}
        y={78}
        fill="currentColor"
        fontSize="18"
        textAnchor="middle"
      >
        0
      </text>
      <text
        x={xa}
        y={78}
        fill="currentColor"
        fontSize="18"
        textAnchor="middle"
      >
        {String(a)}
      </text>
    </svg>
  );
}

// ===================== generator =====================

export function generateTask() {
  const a = randInt(-9, 9);
  const correct = Math.abs(a);

  return {
    type: "AbsIntValueTask",
    data: { a, correct },
    tags: ["wartość_bezwzględna", "definicja"],
  };
}

// ===================== rendering =====================

export function renderTask({ data }) {
  const { a } = data;

  return (
    <div className="space-y-3" style={{ textAlign: "left", paddingBottom: 18 }}>
      <p style={{ marginBottom: 0 }}>
        Oblicz wartość:
        <span style={{ marginLeft: 10 }}>
          <InlineMath math={`\\left|${a}\\right|`} />
        </span>
      </p>
    </div>
  );
}

export function renderInput(_, userAnswer, onChange, disabled) {
  const valueStr = toRawAnswer(userAnswer);

  const handleChange = (val) => {
    onChange(toRawAnswer(val));
  };

  return (
    <FractionInput
      value={valueStr}
      onChange={handleChange}
      disabled={disabled}
      wide={true}
      label=""                // ✅ usuwa "a = ..." w podglądzie
      placeholder="Wpisz liczbę"
    />
  );
}


export function checkAnswer({ data }, userAnswer) {
  const given = evalNumber(toRawAnswer(userAnswer));
  if (!Number.isFinite(given)) return false;
  return Math.abs(given - data.correct) < 1e-9;
}

export function renderAnswer({ data }) {
  return <InlineMath math={`${data.correct}`} />;
}

// ✅ ma być jak w innych taskach: bez „Twoja odpowiedź:”
// UI i tak to wypisuje, my zwracamy tylko treść
export function renderUserAnswer(userAnswer) {
  const raw = toRawAnswer(userAnswer);
  if (!raw || String(raw).trim() === "") return <i>(brak odpowiedzi)</i>;
  return <InlineMath math={`${normalize(raw)}`} />;
}

export function renderExplanation({ data }) {
  const { a, correct } = data;

  // prostsza forma: bez -(-a)
  const ruleLatex =
    a < 0 ? `\\left|${a}\\right| = ${correct}` : `\\left|${a}\\right| = ${a}`;

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>
        <strong>Krok 1.</strong> Wartość bezwzględna liczby to jej{" "}
        <strong>odległość od 0</strong> na osi liczbowej, więc zawsze jest
        nieujemna.
      </p>

      <p>
        <strong>Krok 2.</strong> Obliczamy wartość:
      </p>
      <div style={{ textAlign: "center" }}>
        <BlockMath math={ruleLatex} />
      </div>

      <p>
        <strong>Krok 3.</strong> Na osi liczbowej odcinek między{" "}
        <InlineMath math={"0"} /> i <InlineMath math={`${a}`} /> ma długość{" "}
        <InlineMath math={`\\left|${a}\\right|`} />:
      </p>

      <div style={{ display: "flex", justifyContent: "center" }}>
        <NumberLineDistance a={a} />
      </div>

      <p>
        <strong>Wniosek.</strong>{" "}
        <InlineMath math={`\\left|${a}\\right| = ${correct}`} />.
      </p>
    </div>
  );
}
